import styled, { css } from 'styled-components';
import { FontSizes, TextInput } from '../typography/typography.styles';
import {
  MenuItemAppearance,
  MenuItemContentWeight,
  MenuItemSize,
} from './menu-item.component';
import { getEncodedCheckMarkIconSvg } from '../../utils/svg/svg.utils';

export const MenuItems = styled.div`
  display: grid;
  align-content: start;
`;

interface OptionProps {
  $isSelected?: boolean;
  $isDisabled?: boolean;
  $hasSuffix?: boolean;
  $hasPrefix?: boolean;
  $size?: MenuItemSize;
  $appearance?: MenuItemAppearance;
  $contentWeight?: MenuItemContentWeight;
  $showBackgroundOnSelected?: boolean;
}

const getSizeConfig = (size: MenuItemSize) =>
  ({
    small: css`
      --padding-vertical: 0.8rem;
      --padding-horizontal: 1.2rem;

      font-size: 1.2rem;
    `,
    medium: css`
      --padding-vertical: 1.2rem;
      --padding-horizontal: 1.2rem;

      font-size: 1.6rem;
    `,
  })[size];

export const Container = styled.div`
  padding: 4px 0;
`;

export const MenuItemInner = styled.button<OptionProps>`
  --gap: 1.2rem;

  background: none;
  border: none;
  cursor: pointer;
  padding: var(--padding-vertical) var(--padding-horizontal);
  display: grid;
  align-items: center;
  gap: var(--gap);
  color: ${({ theme }) => theme.colors.baseColors.grey150};
  ${TextInput}
  font-size: ${FontSizes.medium};
  cursor: pointer;
  position: relative;
  transition: background-color 0.2s;
  border-radius: 6px;
  text-align: left;
  ${({ $size = 'medium' }) => getSizeConfig($size)}
  width: 100%;
  font-weight: ${({ $contentWeight = 400 }) => $contentWeight};

  ${({ $hasSuffix, $hasPrefix }) => css`
    grid-template-columns: ${$hasPrefix ? 'auto' : ''} 1fr ${$hasSuffix
        ? 'auto'
        : ''};
  `}

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      cursor: auto;
      color: #96999e;
      pointer-events: none;
    `}

  ${({ $isDisabled }) =>
    !$isDisabled &&
    css`
      &:hover {
        background-color: ${({ theme }) => theme.colors.baseColors.grey5};
      }

      &:active {
        background-color: ${({ theme }) =>
          theme.colors.passwordValidationMarker.backgroundColor};
      }
    `}

    ${({ $isSelected, $showBackgroundOnSelected }) =>
    $isSelected &&
    $showBackgroundOnSelected &&
    css`
      background-color: ${({ theme }) => theme.colors.baseColors.grey5};
    `}

    ${({ $isSelected }) =>
    $isSelected &&
    css`
      color: ${({ theme }) => theme.colors.namedColors.primary};
      svg {
        color: ${({ theme }) => theme.colors.namedColors.primary};
      }

      &:hover {
        background-color: #f1f2f4;
      }
    `}

    ${({ $isSelected, $hasSuffix }) =>
    $isSelected &&
    !$hasSuffix &&
    css`
      --size: 1em;

      padding-right: calc(var(--gap) + var(--size) + var(--padding-horizontal));
      &::after {
        content: '';
        position: absolute;
        top: 50%;
        right: var(--padding-horizontal);
        transform: translateY(-50%);
        width: var(--size);
        height: var(--size);
        background-image: ${({ theme }) =>
          `url(${getEncodedCheckMarkIconSvg(theme.colors.namedColors.primary)})`};
        background-size: contain;
        background-repeat: no-repeat;
      }
    `}

  ${({ $appearance }) =>
    $appearance === 'danger' &&
    css`
      background-color: ${({ theme }) => theme.colors.baseColors.red5};
      border: 1px solid ${({ theme }) => theme.colors.baseColors.red100};
      color: ${({ theme }) => theme.colors.baseColors.red100};

      &:hover,
      &:active {
        background-color: ${({ theme }) => theme.colors.baseColors.red5};
      }
    `};

  ${({ $appearance }) =>
    $appearance === 'warning' &&
    css`
      background-color: ${({ theme }) => theme.colors.baseColors.yellow5};
      border: 1px solid ${({ theme }) => theme.colors.baseColors.yellow100};
      color: ${({ theme }) => theme.colors.baseColors.yellow200};

      &:hover,
      &:active {
        background-color: ${({ theme }) => theme.colors.baseColors.yellow5};
      }
    `};
`;

export const Prefix = styled.span`
  display: flex;
  width: 1.25em;
  height: 1.25em;
  color: ${({ theme }) => theme.colors.baseColors.grey150};

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const MenuItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  min-width: 0;
`;

export const ProhibitionReason = styled.span<{
  $appearance: MenuItemAppearance;
}>`
  color: ${({ $appearance, theme }) =>
    $appearance === 'warning'
      ? theme.colors.baseColors.yellow150
      : theme.colors.baseColors.red130};
  font-size: ${FontSizes.small};
  font-weight: 500;
  line-height: 1.6rem;
  overflow: hidden;
`;

export const Suffix = styled.span`
  display: flex;
`;
